import type { ExtraFieldModel } from '../domain/field-model.js'
import { isBag, isTechnicalKey } from '../domain/humane.js'
import { ValueEditor } from './ValueEditor.js'

interface OtherFieldsSectionProps {
  fields: readonly ExtraFieldModel[]
  values: Record<string, unknown>
  onChange: (path: string, value: unknown) => void
  disabled?: boolean
  /** Fields already shown elsewhere on the page (the title, the picture, the search-engine group). */
  skip?: ReadonlySet<string>
  /** Keys of an object that another section edits, per top-level field (`extra` → `seo`). */
  omitInside?: Readonly<Record<string, ReadonlySet<string>>>
  bodyFormat?: string
}

/**
 * Data the content type does not declare — everything the old site brought
 * along (`card`, `landing`, `extra`…). It is not lost and not shown as JSON:
 * every key becomes a field, a group or a list, named in words.
 */
export function OtherFieldsSection({ fields, values, onChange, disabled, skip, omitInside, bodyFormat }: OtherFieldsSectionProps) {
  const shown = fields.filter((field) => !isTechnicalKey(field.path) && !skip?.has(field.path))
  // Simple fields first (the body, the dates), then the groups: the long structures end up at the bottom.
  const composite = (path: string) => isBag(values[path]) || Array.isArray(values[path])
  const ordered = [...shown.filter((field) => !composite(field.path)), ...shown.filter((field) => composite(field.path))]
  if (ordered.length === 0) return null

  return <>
    {ordered.map((field) => <ValueEditor
      key={field.path}
      name={field.path}
      value={values[field.path]}
      siblings={values}
      depth={1}
      omit={omitInside?.[field.path]}
      bodyFormat={bodyFormat}
      disabled={disabled}
      onChange={(next) => onChange(field.path, next)}
    />)}
  </>
}
